// migrated 1:1 from @nexato/nx-core-module, #688
import { Component, ChangeDetectionStrategy, Input, forwardRef, signal, computed } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SortField {
  label: string;        // Display label: "Created date", "Updated date"
  fieldName: string;    // Backend field name: "createdDate", "lastModifiedDate"
  key: string;          // Unique identifier
}

export interface SortValue {
  field: SortField;
  direction: 'asc' | 'desc';
}

// Constant for "Keine Sortierung" option
const NO_SORT_OPTION: SortField = {
  label: 'Keine Sortierung',
  fieldName: 'id',
  key: 'id'
};

@Component({
  selector: 'nx-sort-select',
  templateUrl: './nx-sort-select.html',
  styleUrls: ['./nx-sort-select.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NxSortSelectComponent),
      multi: true
    }
  ]
})
export class NxSortSelectComponent implements ControlValueAccessor {
  // Inputs
  private _options: SortField[] = [];
  @Input() set options(value: SortField[]) {
    this._options = value;
    this.updateOptionsWithNoSort();
  }
  get options(): SortField[] {
    return this._options;
  }

  @Input() showNoSortOption: boolean = false;
  @Input() placeholder: string = 'Sortierung';
  @Input() appendTo: string = 'body';
  @Input() className: string = '';

  // Computed options with "Keine Sortierung" prepended if enabled
  readonly effectiveOptions = signal<SortField[]>([]);

  // Internal state signals
  readonly selectedField = signal<SortField | null>(null);
  readonly direction = signal<'asc' | 'desc'>('desc');

  // Check if current selection is "Keine Sortierung"
  readonly isNoSortSelected = computed(() => {
    const field = this.selectedField();
    return field?.key === NO_SORT_OPTION.key;
  });

  // Computed value for external binding
  readonly value = computed<SortValue | null>(() => {
    const field = this.selectedField();
    if (!field) return null;
    // For "Keine Sortierung", always return 'desc'
    const effectiveDirection = field.key === NO_SORT_OPTION.key ? 'desc' : this.direction();
    return {
      field,
      direction: effectiveDirection
    };
  });

  // ControlValueAccessor callbacks
  private onChange: (value: SortValue | null) => void = () => {};
  private onTouched: () => void = () => {};

  // Update effective options when showNoSortOption or options change
  private updateOptionsWithNoSort(): void {
    const opts = this.showNoSortOption
      ? [NO_SORT_OPTION, ...this._options]
      : this._options;
    this.effectiveOptions.set(opts);
  }

  // Write value from outside (ngModel)
  writeValue(value: SortValue | null): void {
    if (value) {
      this.selectedField.set(value.field);
      this.direction.set(value.direction);
    } else {
      this.selectedField.set(null);
      this.direction.set('desc');
    }
  }

  // Register callback for value changes
  registerOnChange(fn: (value: SortValue | null) => void): void {
    this.onChange = fn;
  }

  // Register callback for touch events
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Handle field selection change from dropdown
  onFieldChange(field: SortField): void {
    this.selectedField.set(field);
    this.emitValue();
    this.onTouched();
  }

  // Toggle sort direction (disabled for "Keine Sortierung")
  onDirectionToggle(): void {
    // Don't toggle if "Keine Sortierung" is selected
    if (this.isNoSortSelected()) {
      return;
    }
    this.direction.set(this.direction() === 'asc' ? 'desc' : 'asc');
    this.emitValue();
    this.onTouched();
  }

  // Emit combined value
  private emitValue(): void {
    const currentValue = this.value();
    this.onChange(currentValue);
  }

  // Get icon class for direction button
  getDirectionIcon(): string {
    return this.direction() === 'asc' ? 'icon icon-arrow-up' : 'icon icon-arrow-down';
  }

  // Get tooltip for direction button
  getDirectionTooltip(): string {
    return this.direction() === 'asc' ? 'Aufsteigend' : 'Absteigend';
  }
}
